<template>
  <div class="aco-grid">
    <div class="aco-info-box">
      <b-card>
        <margin-fields :fields="marginFields()"/>
        <b-button block v-on:click="licenseSearch()">{{ $t('licenseSearch')}}</b-button>
        <b-button block v-on:click="orderSearch()">{{ $t('orderSearch')}}</b-button>
        <b-button block v-on:click="userSearch()">{{ $t('userSearch')}}</b-button>
        <b-button block v-on:click="groupSearch()">{{ $t('groupSearch')}}</b-button>
        <b-button block v-on:click="usageSearch()">{{ $t('usageSearch')}}</b-button>
        <b-button block :href="getFeideStatisticsLink()" target="_blank">{{ $t('feideStatistics')}}</b-button>
        <b-button v-if="isNsrSource" block :href="getNsrData()" target="_blank">{{ $t('nsr')}}</b-button>
        <b-button block v-on:click="setActiveOrganization()" :disabled="isActiveProfileSet">{{ $t('setActiveOrganization')}}</b-button>
        <b-button v-if="!isNsrSource" type="submit" variant="danger" @click="deleteOrganization()" block>{{ $t('deleteOrganization') }}</b-button>
        <delete-organization-modal ref="confirmOrganizationDelete"/>
      </b-card>
    </div>

    <b-col>
      <div class="aco-details-box">
        <b-col class="aco-details">
          <details-form v-if="isNsrSource" :fields="nsrOrganizationFields()" :state-object="organization"/>
          <details-form v-else :fields="localOrganizationFields()" :state-object="organization"/>
          <b-row>
            <b-col md="12">
              <b-button type="button" class="float-left mt-3" @click="reloadOrganization()">{{ $t('reset') }}</b-button>
              <b-button type="button" variant="primary" class="float-right mt-3" @click="saveOrganization()">{{ $t('save') }}</b-button>
            </b-col>
          </b-row>
        </b-col>
      </div>

      <div class="aco-details-box">
        <b-col class="aco-details">
          <b-row>
            <b-col class="mb-2">
              <b-button type="button" variant="primary" class="float-right" @click.prevent="openCreateDepartmentModal">
                {{ $t('departmentCreate') }}
              </b-button>
              <h5 class="float-left">{{ $t('departments') }}</h5>
            </b-col>
          </b-row>
          <template>
            <b-table show-empty striped hover small class="aco-table" :items="sortedDepartments" :fields="departmentFields">
              <template slot="empty">
                <div class="text-center">{{ $t('departmentsMissing') }}</div>
              </template>
              <template v-slot:cell(actions)="row">
                <router-link v-if="row.item.organizationNumber && row.item.departmentId" :to="{ name: 'DepartmentDetails', params: {
                  id: row.item.organizationNumber,
                  departmentId: row.item.departmentId
                }}">
                  <b-button size="md" class="py-0 float-right">{{ $t('details') }}</b-button>
                </router-link>
              </template>
            </b-table>
          </template>
        </b-col>
      </div>

      <b-modal :id="`create-department-${organization.organizationNumber}`" no-stacking>
        <template slot="modal-title">{{ $t('departmentCreate') }}</template>
        <b-form v-if="newDepartment" @submit.prevent="createDepartment(newDepartment)">
          <fieldset :disabled="isLoading" style="display: flex; flex-direction: column; gap: 1rem;">
            <b-form-input
              id="departmentId"
              v-model="newDepartment.departmentId"
              :placeholder="$t('departmentId')"
              :autofocus="true"
            />
            <b-alert
              variant="warning"
              style="margin-bottom: 0"
              :show="!isUniqueDepartment"
            >{{ $t('uniqueDepartmentId') }}</b-alert>
            <b-form-input
              id="name"
              v-model="newDepartment.name"
              :placeholder="$t('name')"
            />
            <b-form-input
              id="name"
              v-model="newDepartment.organizationNumber"
              :placeholder="$t('organizationNumber')"
              :disabled="true"
              hidden
            />
            <b-form-select
              id="organizationSource"
              v-model="newDepartment.organizationSource"
              :placeholder="$t('organizationSource')"
              :options="['LOCAL', 'NSR', 'FEIDE']"
            />
          </fieldset>
          <input type="submit" hidden />
        </b-form>
        <template slot="modal-footer">
          <b-button type="submit" variant="primary" :disabled="isLoading || !canCreateDepartment" @click.prevent="createDepartment(newDepartment)">
            {{ $t('create') }}
          </b-button>
          <b-button variant="secondary" class="float-lg-right" @click="$bvModal.hide(`create-department-${organization.organizationNumber}`)" :disabled="isLoading">
            {{ $t('cancel') }}
          </b-button>
        </template>
      </b-modal>

      <div class="aco-details-box">
        <b-col class="aco-details">
          <b-row>
            <b-col>
              <h5>{{ $t('discount') }}</h5>
            </b-col>
          </b-row>
          <template>
            <b-table show-empty striped hover small class="aco-table" :items="discounts" :fields="discountFields()">
              <template slot="empty">
                <div class="text-center">{{ $t('noDiscounts')}}</div>
              </template>
            </b-table>
          </template>
        </b-col>
      </div>
    </b-col>
  </div>
</template>

<script>
  import BaseComponent from '../../components/BaseComponent'
  import { mapGetters } from 'vuex'
  import MarginFields from '../../components/MarginFields'
  import DetailsForm from '../../components/DetailsForm'
  import DeleteOrganizationModal from './DeleteOrganizationModal'
  import DiscountService from '../../services/DiscountService'

  export default {
    name: 'OrganizationDetails',
    extends: BaseComponent,
    components: {
      DeleteOrganizationModal,
      MarginFields,
      DetailsForm
    } ,

    data () {
      return {
        discounts: [],
        isLoading: false,
        newDepartment: null,
      }
    },
    created () {
      this.$store.dispatch('organizations/getOrganization', this.$route.params.id)
      this.$store.dispatch('organizations/getDepartments', this.$route.params.id)
      //this.$store.dispatch('organizations/resetDepartment')
      this.getDiscounts(this.$route.params.id)
    },
    computed: {
      ...mapGetters('organizations', ['organization', 'departments', 'department']),
      ...mapGetters('sessionStorage', ['isActiveProfileSet', 'activeProfile']),
      isNsrSource () {
        return (this.organization.organizationSource === 'NSR')
      },
      departments() {
        return this.organization.departments || [];
      },
      sortedDepartments() {
        return (this.departments || []).toSorted((a, b) => a.name.localeCompare(b.name))
      },
      isUniqueDepartment() {
        return !this.newDepartment?.departmentId
          || this.departments.every(({ departmentId }) => departmentId !== this.newDepartment.departmentId)
      },
      canCreateDepartment() {
        return this.isUniqueDepartment && !!this.newDepartment?.name
      },
      departmentFields() {
        return [
          { key: 'name', width: 3, label: this.$t('name') },
          { key: 'departmentId', width: 1, label: this.$t('departmentId') },
          { key: 'organizationSource',width: 1, label: this.$t('departmentSource'), formatter: value => value ? this.$t(`organizationSources.${value}`) : '' },
          { key: 'users', width: 1, label: this.$t('users'), formatter: value => value ? value.length : 0 },
          { key: 'actions', label: '' }
        ]
      },
    },
    methods: {
      getFeideStatisticsLink () {
        return 'https://kunde.feide.no/org/23547/stats/logins/sp/1723883?filter=' + this.organization.organizationNumber + '&inspect=school'
      },
      userSearch () {
        this.$store.dispatch('users/resetFilter')
        this.$store.dispatch('users/updateOrganization', this.$route.params.id)
          .then(() => {
            this.$router.push('/users')
          })
      },
      orderSearch () {
        this.$store.dispatch('orders/resetFilter')
        this.$store.dispatch('orders/updateOrganization', this.$route.params.id)
          .then(() => {
            this.$router.push('/orders')
          })
      },
      marginFields () {
        return [
          { name: 'source', displayName: this.$t('source'), value: this.translateAlternatives('organizationSources', this.organization.organizationSource) },
          { name: 'sourceId', displayName: this.$t('sourceId'), value: this.organization.sourceId },
        ]
      },
      localOrganizationFields () {
        return [
          { type: 'header', backButton: true, width: 12, displayName: this.$t('organizationInformation') },
          { type: 'text', name: 'name', width: 3, displayName: this.$t('organizationName') },
          { type: 'text', name: 'characteristic', width: 3, displayName: this.$t('characteristic') },
          { type: 'text', name: 'organizationNumber', width: 3, readonly: true },
          { type: 'text', name: 'municipalityName', width: 3 },
          { type: 'number', name: 'numberOfPupils', width: 3, displayName: this.$t('numberOfPupils') },
          { type: 'number', name: 'numberOfPupilsOverride', width: 3, displayName: this.$t('numberOfPupilsOverride') },
          { type: 'boolean', name: 'active', width: 2 },
          { type: 'boolean', name: 'school', width: 2 },
          { type: 'boolean', name: 'schoolOwner', width: 2 },
          { type: 'number', name: 'schoolStepFrom', displayName: this.$t('gradesFrom'), width: 2 },
          { type: 'number', name: 'schoolStepTo', displayName: this.$t('gradesTo'), width: 2 },
          { type: 'number', name: 'minimumLicenses', displayName: this.$t('minimumLicenses'), readonly: true, width: 2 }
        ]
      },
      nsrOrganizationFields () {
        return [
          { type: 'header', backButton: true, width: 12, displayName: this.$t('organizationInformation') },
          { type: 'text', name: 'name', readonly: true, width: 3, displayName: this.$t('organizationName') },
          { type: 'text', name: 'characteristic', readonly: true, width: 3, displayName: this.$t('characteristic') },
          { type: 'text', name: 'organizationNumber', readonly: true, width: 3 },
          { type: 'text', name: 'municipalityName', readonly: true, width: 3 },
          { type: 'number', name: 'numberOfPupils', readonly: true, width: 3, displayName: this.$t('numberOfPupilsNsr') },
          { type: 'number', name: 'numberOfPupilsOverride', width: 3, displayName: this.$t('numberOfPupilsOverride') },
          { type: 'boolean', name: 'active', readonly: true, width: 2 },
          { type: 'boolean', name: 'school', readonly: true, width: 2 },
          { type: 'boolean', name: 'schoolOwner', readonly: true, width: 2 },
          { type: 'number', name: 'schoolStepFrom', displayName: this.$t('gradesFrom'), readonly: true, width: 2 },
          { type: 'number', name: 'schoolStepTo', displayName: this.$t('gradesTo'), readonly: true, width: 2 },
          { type: 'number', name: 'minimumLicenses', displayName: this.$t('minimumLicenses'), readonly: true, width: 2 }
        ]
      },
      getNsrData () {
        return 'https://nsr.udir.no/enhet/' + this.organization.sourceId
      },
      saveOrganization () {
        this.$store.dispatch('organizations/create', this.$route.params.id)
      },
      reloadOrganization () {
        this.$store.dispatch('organizations/getOrganization', this.$route.params.id)
      },
      deleteOrganization () {
        this.$root.$emit('bv::show::modal', 'confirmOrganizationDelete')
      },
      licenseSearch()    {
        this.$store.dispatch('licenses/resetFilter')
        let payload = {
          customerOrganizationNumber : this.$route.params.id,
        }
        this.$store.dispatch('licenses/getLicensesByCustomerOrgNumberOrUserIdOrEan', payload)
          .then(() => {
            this.$router.push('/licenses')
          })
      },
      groupSearch () {
        this.$store.dispatch('groups/resetFilter')
        this.$store.dispatch('groups/getGroupsByOwnerOrganizationNumber', this.$route.params.id)
          .then(() =>{
            this.$router.push('/groups')
          })
      },
      usageSearch () {
        this.$store.dispatch('usage/resetFilter')
        this.$store.dispatch('usage/updateOrganization', this.$route.params.id)
          .then(() => {
            this.$router.push('/usage')
          })
      },
      setActiveOrganization () {
        let sessionStorageRequest = {
          activeProfileType: 'organization',
          id: this.$route.params.id,
          name: this.organization.name
        }
        this.$store.dispatch('sessionStorage/updateSessionStorage', sessionStorageRequest)
        this.$store.dispatch('sessionStorage/updateState')
      },
      getDiscounts (organizationNumber) {
        DiscountService.getDiscounts(organizationNumber)
          .then((response) => {
            this.discounts = response
          })
      },
      discountFields () {
        return [
          { key: 'type', label: this.$t('discountType'), tdClass: 'text-nowrap' },
          { key: 'eans', label: this.$t('eans'), formatter: value => this.formatList(value) }
        ]
      },
      openCreateDepartmentModal () {
        this.newDepartment = {
          departmentId: '',
          name: null,
          organizationNumber: this.$route.params.id,
          organizationSource: this.organization?.organizationSource,
        }
        this.$bvModal.show(`create-department-${this.organization.organizationNumber}`)
      },
      createDepartment (department) {
        if (!this.canCreateDepartment) {
          return
        }
        try {
          this.$store.dispatch('organizations/createDepartment', department)
          this.$bvModal.hide(`create-department-${this.organization.organizationNumber}`)
          //this.$store.dispatch('organizations/resetDepartment')
          // @todo success
        } catch (error) {
          // @todo error
        }
      }
    },
    i18n: {
      messages: {
        en: {
          'deleteOrganization': 'Delete organization',
          'organizationInformation': 'Organization information',
          'numberOfPupils': 'Number of pupils',
          'numberOfPupilsNsr': 'Number of pupils (NSR)',
          'numberOfPupilsOverride': 'Number of pupils (override)',
          'nsr': 'NSR',
          'setActiveOrganization': 'Set active organization',
          'discount': 'Discount',
          'discountType': 'Discount type',
          'noDiscounts': 'No discounts',
          'gradesFrom': 'Grades from',
          'gradesTo': 'Grades to',
          'minimumLicenses': 'Minimum licenses',
          'uniqueDepartmentId': 'Department number must be unique',
          'departmentId': 'Department number',
          'departments': 'Departments',
          'departmentName': 'Department name',
          'departmentSource': 'Department source',
          'departmentsMissing': 'No departments',
          'departmentCreate': 'Create department',
          'organizationName': 'Organization name',
          'organizationSource': 'Organization source',
        },
        nb: {
          'deleteOrganization': 'Slett organisasjon',
          'organizationInformation': 'Organisasjonsinformasjon',
          'numberOfPupils': 'Antall elever',
          'numberOfPupilsNsr': 'Antall elever (NSR)',
          'numberOfPupilsOverride': 'Antall elever (overstyrt)',
          'nsr': 'NSR',
          'setActiveOrganization': 'Sett aktiv organisasjon',
          'discount': 'Rabatt',
          'discountType': 'Rabattype',
          'noDiscounts': 'Ingen rabatter',
          'gradesFrom': 'Årstrinn fra',
          'gradesTo': 'Årstrinn til',
          'minimumLicenses': 'Minimum lisenser',
          'uniqueDepartmentId': 'Avdelingsnummer må være unikt',
          'departmentId': 'Avdelingsnummer',
          'departments': 'Avdelinger',
          'departmentName': 'Avdelingsnavn',
          'departmentSource': 'Avdelingskilde',
          'departmentsMissing': 'Ingen avdelinger',
          'departmentCreate': 'Opprett avdeling',
          'organizationName': 'Organisasjonsnavn',
          'organizationSource': 'Organisasjonskilde',
        }
      }
    },
  }
</script>
